import React, { ReactElement, useEffect, useId, useRef } from 'react'
import Header from 'src/components/Header/Header'
import type { PhysicalCardLifecycleControls } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { useTranslate } from 'src/localization/Trns'

import CardActionsMenu from './CardActionsMenu/CardActionsMenu'
import {
  type PhysicalCardActionsMenuOptions,
  physicalCardActionsMenuPropsFor,
} from './CardActionsMenu/physicalCardActionsMenuPropsFor'
import CardArt from './CardArt/CardArt'
import * as style from './CardDetailHero.style'
import type { PhysicalCardManagementStatus } from './cardDetailHeroTypes'
import CardDisplayActions from './CardDisplayActions/CardDisplayActions'
import CardHeroCompact from './CardHeroCompact/CardHeroCompact'
import CardRevealPanel from './CardRevealPanel/CardRevealPanel'
import CardSummary from './CardSummary/CardSummary'
import { useCardDetailHeroData } from './useCardDetailHeroData'
import { useCardRevealDisclosure } from './useCardRevealDisclosure'
import { useCompactHeroSwap } from './useCompactHeroSwap'

type CardDetailHeroProps = {
  /**
   * The card this hero describes. The hero is a feature, so it never resolves
   * the id itself — the page owns routing and passes it down (root CLAUDE.md
   * layering, eslint-enforced). The whole credit-card model is deliberately NOT
   * passed: it is a legacy class-backed model, and class instances must not
   * cross into query-cache-backed code.
   */
  creditCardId: string | undefined
  /**
   * The authoritative Card Management status for this card, resolved by the
   * page from `physicalCardManagementQueries.status` — the SAME value it hands
   * the redesigned settings surface, and the same one the lifecycle controller
   * derives `freezeAction` from.
   *
   * Freeze/unfreeze writes land on the Card Management status resource, not on
   * the legacy `/creditcards/{id}` record, so the status mapped off that record
   * (below) does not move when the user freezes. Reading it here was what left
   * the hero's pill and card art claiming ACTIVE while the settings tile had
   * already flipped to "Unfreeze". Undefined only until that query resolves, or
   * on a card with no Card Management status at all, where the mapped legacy
   * status is still the best answer we have.
   */
  cardManagementStatus: PhysicalCardManagementStatus | undefined
  /** Page-owned location signal used only to clear sensitive reveal state. */
  navigationKey: string
  /**
   * Whether this user holds `canUpdateCreditCard` for this card. The page
   * already resolves the card's permissions for its other actions, so the hero
   * reads the same answer rather than issuing its own permissions query.
   */
  canUpdateCard: boolean
  /**
   * Runtime banner offset, read from redux by the page (the feature stays
   * redux-free). Drives both the swap threshold and the pinned compact bar's
   * fixed `top`, so a banner opening shifts them together.
   */
  fixedElementPushdown: number
  /**
   * Whether the sidenav is locked open, also read from redux by the page. Lets
   * the pinned compact bar inset from the left by the sidenav's reserved width
   * so its leading content is not hidden behind the fixed sidenav.
   */
  sideNavLockedOpen: boolean
  lifecycleControls: PhysicalCardLifecycleControls
  /**
   * The ••• menu's two non-lifecycle actions, both owned by the page: Adjust
   * limit is a route change, and the Add to Wallet verdict needs the signed-in
   * user. Threaded verbatim into both hero variants so the full hero and the
   * pinned compact bar offer exactly the same menu.
   */
  actionsMenuOptions: PhysicalCardActionsMenuOptions
}

const CardDetailHero: React.FC<CardDetailHeroProps> = ({
  creditCardId,
  cardManagementStatus,
  navigationKey,
  canUpdateCard,
  fixedElementPushdown,
  sideNavLockedOpen,
  lifecycleControls,
  actionsMenuOptions,
}): ReactElement => {
  const translate = useTranslate()

  const { card, cardDetailsAvailability, cardDetailsCapabilityError } =
    useCardDetailHeroData(creditCardId)

  // The full hero's reveal-panel id. Per-instance (`useId`, review finding #10)
  // rather than a module constant: the compact bar mounts its own panel with its
  // own id, so neither Show details trigger's aria-controls can resolve to the
  // wrong panel while both heroes are mounted.
  const revealPanelId = useId()

  // The Card Management status is the authoritative one and wins whenever it is
  // resolved: it is the resource freeze/unfreeze actually writes, so it is the
  // only source that moves when the user acts. The status mapped off the legacy
  // credit-card record is the fallback for a card that has no Card Management
  // status, and for the window before that query answers — never a second
  // opinion about a card that has one.
  const status = cardManagementStatus ?? card?.status

  // The shell also owns the reveal disclosure (same "one owner" rule); both the
  // full hero and the compact bar share it, anchored to whichever opened it.
  const reveal = useCardRevealDisclosure({
    creditCardId,
    cardDetailsAvailability,
    cardDetailsCapabilityError,
    navigationKey,
  })
  const { close: closeReveal } = reveal

  // Text for the persistent reveal announcer rendered below.
  //
  // NEVER interpolate `reveal.details` into this. A live region is read aloud,
  // so it is the one surface in this feature where a secret could escape
  // without ever being drawn on screen — the announcement says *that* details
  // appeared, never what they are.
  //
  // `error` is deliberately silent here: the panel announces it with
  // `role="alert"`, which is both assertive and reliable on insertion. Emitting
  // it in both places would just say it twice.
  const revealAnnouncement = ((): string => {
    switch (reveal.state) {
      case 'loading':
        return translate('Loading secure card details')
      case 'ready':
        return translate('Card details are now shown')
      case 'unavailable':
        return translate('Card details are unavailable for this card')
      case 'error':
      case 'idle':
        return ''
    }
  })()

  // The full→compact swap: observe the full hero's bottom edge and flip once it
  // passes under the global header. Both heroes stay mounted; the off-screen one
  // is inert.
  const fullHeroRef = useRef<HTMLElement>(null)
  const compactOpen = useCompactHeroSwap(fullHeroRef, fixedElementPushdown)

  // Close the reveal across any swap so a panel is never left anchored to a
  // now-inert hero (plan §3 rework #2 / §5). `closeReveal` is stable.
  useEffect(() => {
    closeReveal()
  }, [compactOpen, closeReveal])

  return (
    // Reuse the legacy Header layout so the hero occupies the exact page band
    // (white background + standard responsive header padding) the AccountHeader
    // it replaces did — keeping horizontal alignment and the gap to the Subnav
    // below identical across breakpoints, rather than re-deriving that padding.
    <Header>
      {/*
        The reveal's announcements, in ONE region that outlives them.

        Screen readers announce changes *inside a region that already existed*;
        a region inserted with its text already in place is frequently silent.
        The panel can only ever be the second kind — it mounts on open, and
        `loading → ready` swaps one CardRevealPanel element for another, so the
        region is destroyed at exactly the moment it would have spoken.

        So the announcement is decoupled from the panel: this region is mounted
        for the hero's whole life and only its text changes. It sits outside the
        full hero because that `<section>` goes `aria-hidden` + `inert` once the
        compact bar takes over, which would silence a region inside it. One
        region serves both variants — the disclosure hook is already shared, so
        a per-variant region would only risk announcing twice.
      */}
      <div
        css={style.visuallyHidden}
        role="status"
        data-testid="card-reveal-announcer"
      >
        {revealAnnouncement}
      </div>
      <section
        ref={fullHeroRef}
        css={style.hero}
        data-testid="card-detail-hero"
        aria-hidden={compactOpen}
        // Once the compact bar takes over, the full hero has scrolled under the
        // global header; make it inert so its (now duplicated) controls leave the
        // a11y tree and tab order while it stays mounted for the jank-free swap.
        {...(compactOpen ? { inert: '' } : {})}
      >
        <div css={style.left} data-testid="card-detail-hero-left">
          <CardSummary
            creditCardId={creditCardId}
            status={status}
            canUpdateCard={canUpdateCard}
            // The full hero goes inert once the compact bar takes over, so it
            // hands the live-region role across with it.
            announceStates={!compactOpen}
          />
        </div>
        <div
          css={style.right}
          data-testid="card-detail-hero-right"
          ref={reveal.containerRefFor('full')}
        >
          <div css={style.toolbar} data-testid="card-detail-hero-toolbar">
            <CardDisplayActions
              freezeAction={lifecycleControls.freezeAction}
              onFreeze={() => void lifecycleControls.toggleFreeze('cardHero')}
              canFreeze={lifecycleControls.canToggleFreeze}
              freezePending={lifecycleControls.isFreezePending}
              revealOpen={reveal.isOpenFor('full')}
              onToggleReveal={() => reveal.toggle('full')}
              cardDetailsAvailability={cardDetailsAvailability}
              cardDetailsCapabilityError={cardDetailsCapabilityError}
              revealPanelId={revealPanelId}
            />
            <CardActionsMenu
              {...physicalCardActionsMenuPropsFor(
                lifecycleControls,
                'cardHero',
                actionsMenuOptions,
              )}
            />
          </div>
          {/*
            The art is pure identity, so it waits for the identity group rather
            than rendering a card face full of blanks. CardSummary announces the
            group's loading/error state in text — one announcement, not two.
            Protected values stay masked until the imperative reveal succeeds.
          */}
          {card !== undefined && (
            <CardArt
              status={status}
              network={card.network}
              last4={card.last4}
              holder={card.cardholder.displayName}
              imageUrl={card.cardArt.imageUrl}
              textColor={card.cardArt.textColor}
              textShadow={card.cardArt.textShadow}
              pan={reveal.details?.pan ?? ''}
              exp={reveal.details?.expiry ?? ''}
              revealed={reveal.isOpenFor('full') && reveal.state === 'ready'}
            />
          )}
          {reveal.isOpenFor('full') &&
            reveal.state === 'ready' &&
            reveal.details !== undefined && (
              <CardRevealPanel
                id={revealPanelId}
                state="ready"
                {...reveal.details}
              />
            )}
          {reveal.isOpenFor('full') &&
            reveal.state !== 'idle' &&
            reveal.state !== 'ready' && (
              <CardRevealPanel id={revealPanelId} state={reveal.state} />
            )}
        </div>
      </section>

      {/*
        The pinned condensed hero. Always mounted (inert until the swap opens it),
        so its ids are unique per variant and its reveal is a distinct anchor. It
        is `position: fixed`, rendered as a sibling of the full hero.
      */}
      <CardHeroCompact
        open={compactOpen}
        fixedElementPushdown={fixedElementPushdown}
        sideNavLockedOpen={sideNavLockedOpen}
        creditCardId={creditCardId}
        status={status}
        lifecycleControls={lifecycleControls}
        actionsMenuOptions={actionsMenuOptions}
        revealOpen={reveal.isOpenFor('compact')}
        onToggleReveal={() => reveal.toggle('compact')}
        cardDetailsAvailability={cardDetailsAvailability}
        cardDetailsCapabilityError={cardDetailsCapabilityError}
        revealState={reveal.state}
        revealDetails={reveal.details}
        revealContainerRef={reveal.containerRefFor('compact')}
      />
    </Header>
  )
}

export default CardDetailHero
